import { useState } from "react"
import classNames from "classnames"
import SelectTileType from "./SelectTileType"
import { useOverview } from "web/hooks/data"
import TileSettings from "./TileSettings"
import { TileSummarySettingsModal } from "../Tiles/SummaryTiles/TileSummarySettingsModal"
import { TileTableSettingsModal } from "../Tiles/TableTiles/Modal/TileTableSettingsModal"
import TileChartSettingsModalWrapper from "../Tiles/ChartTiles/Wrappers/TileChartSettingsModalWrapper"
import { TileRichTextSettingsModal } from "../Tiles/OtherTiles/RichText/TileRichTextSettingsModal"
import { TileMapSettingsModal } from "../Tiles/OtherTiles/Map/TileMapSettingsModal"
import { TileMilestoneSettingsModal } from "../Tiles/SummaryTiles/TileMilestoneSettingsModal"
import { ViewableMarketingSummarySettingsModal } from "../Tiles/OtherTiles/ViewableMarketingSummarySettingsModal"
import {
  openSSRTileSettingsModal,
  supportsSSRSettingsModal,
} from "../Tiles/helpers"
import TileGallerySettingsModal from "../Tiles/OtherTiles/TileGallerySettingsModal"

const TILE_WIZARD_SETTINGS_COMPONENT = {
  quick_picks: TileSummarySettingsModal,
  tables: TileTableSettingsModal,
  charts: TileChartSettingsModalWrapper,
  text: TileRichTextSettingsModal,
  map: TileMapSettingsModal,
  gallery: TileGallerySettingsModal,
  milestone: TileMilestoneSettingsModal,
}

const getParamsToAddTile = (tile) => ({
  ...(tile.field_id && {
    overview_tile: { field_id: tile.field_id },
  }),
  ...(tile.topic_id && {
    overview_tile: {
      topic_id: tile.topic_id,
      type: tile.type,
    },
  }),
})

export default function AddTileWizard({
  closeWizardHandler,
  isTemplate,
  overviewFetchParams,
  subjectType,
  subjectId,
}) {
  const overview = useOverview({
    isTemplate,
    overviewFetchParams,
  })
  const { grouped_tile_types: tile_types } = overview
  const defaultTab = tile_types[0].key
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [selectedTile, setSelectedTile] = useState()
  const [tileId, setTileId] = useState()

  async function selectTileHandler(selectedTile) {
    const params = getParamsToAddTile(selectedTile)
    const updatedOverview = await overview.addTile(
      selectedTile.route_suffix,
      params
    )
    const newTiles = updatedOverview.tiles
    const lastTile = newTiles[newTiles.length - 1]
    const newTileId = isTemplate ? lastTile.entryable_id : lastTile.index

    if (supportsSSRSettingsModal(subjectType, selectedTile.key)) {
      const modalFrame = document.querySelector("turbo-frame#modal")

      function handleFrameLoad() {
        modalFrame.removeEventListener("turbo:frame-load", handleFrameLoad)
        closeWizardHandler()
      }

      modalFrame.addEventListener("turbo:frame-load", handleFrameLoad)
      openSSRTileSettingsModal({
        isTemplate,
        overviewId: overview.id,
        tileId: newTileId,
        tileType: selectedTile.key,
        subjectType,
        subjectId,
        fromWizard: true,
      })()
      return
    }

    setTileId(newTileId)
    setSelectedTile(selectedTile)
  }

  async function resetSelectedTileHandler(tile) {
    setSelectedTile(null)
    await tile.deleteTile()
  }

  const activeTileType = tile_types.find((tab) => tab.key === activeTab)

  let TileSettingsComponent

  if (selectedTile?.key === "maps") {
    TileSettingsComponent = TILE_WIZARD_SETTINGS_COMPONENT["map"]
  } else if (selectedTile?.key === "galleries") {
    TileSettingsComponent = TILE_WIZARD_SETTINGS_COMPONENT["gallery"]
  } else if (selectedTile?.key === "milestones") {
    TileSettingsComponent = TILE_WIZARD_SETTINGS_COMPONENT["milestone"]
  } else if (selectedTile?.key === "marketing_summaries") {
    TileSettingsComponent = ViewableMarketingSummarySettingsModal
  } else {
    TileSettingsComponent = TILE_WIZARD_SETTINGS_COMPONENT[activeTab]
  }

  const containerClasses = classNames(
    "flex h-full flex-col overflow-hidden pt-5",
    {
      "px-8": !selectedTile,
    }
  )

  const step = selectedTile ? 2 : 1

  function getTileFetchParams(selectedTile) {
    return {
      id: tileId,
      routeSuffix: selectedTile.route_suffix,
      overviewKey: overview.key,
      parentKeys: overview.refreshKeys,
      width: 1,
    }
  }

  return (
    <div className={containerClasses}>
      <div className="flex flex-1 flex-col overflow-hidden">
        {step === 1 && (
          <SelectTileType
            activeTab={activeTab}
            activeTileType={activeTileType}
            onSelectTileHandler={selectTileHandler}
            setActiveTabHandler={setActiveTab}
            tabs={tile_types}
          />
        )}
        {step === 2 && (
          <TileSettings
            close={closeWizardHandler}
            isTemplate={isTemplate}
            overview={overview}
            resetSelectedTileHandler={resetSelectedTileHandler}
            showBackButton={true}
            subjectType={subjectType}
            subjectId={subjectId}
            tileSettingsComponent={TileSettingsComponent}
            tileFetchParams={getTileFetchParams(selectedTile)}
          />
        )}
      </div>
    </div>
  )
}
